// ============================================================
// MongoDBReplication.jsx
//
// A branded, technical guide to MongoDB Replica Sets,
// Heartbeats, and Automatic Failover.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #00ED64  (MongoDB Spring Green)
//   Accent      → #00E0FF  (Cyan / Sync)
//   Text        → #FFFFFF  (White)
//   Terminal    → #010409  (High-Contrast Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Button,
    Tooltip
} from "@mui/material";

/* MUI Icons */
import SyncIcon from "@mui/icons-material/Sync";
import ShieldIcon from "@mui/icons-material/Shield";
import SensorsIcon from "@mui/icons-material/Sensors";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import StorageIcon from "@mui/icons-material/Storage";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import SpeedIcon from "@mui/icons-material/Speed";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(0, 237, 100, 0.2)",
    borderHover: "rgba(0, 237, 100, 0.6)",
    green: "#00ED64",
    cyan: "#00E0FF",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function NodeCard({ type, label, description, color = BRAND.green }) {
    return (
        <Paper elevation={0} sx={{
            p: 3, background: BRAND.surface, border: `2px solid ${color}`,
            borderRadius: 4, transition: "0.3s", textAlign: 'center',
            "&:hover": { transform: "scale(1.02)", boxShadow: `0 0 20px ${color}20` }
        }}>
            <Typography variant="h6" sx={{ color: color, fontWeight: 900, mb: 1 }}>{type}</Typography>
            <Typography variant="subtitle2" sx={{ color: BRAND.white, mb: 1 }}>{label}</Typography>
            <Typography variant="caption" sx={{ color: BRAND.muted }}>{description}</Typography>
        </Paper>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBReplication() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.green }} />}
                        label="Infrastructure · Module 14"
                        sx={{ bgcolor: "rgba(0,237,100,0.1)", color: BRAND.green, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Replica <span style={{ color: BRAND.green }}>Sets</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        In production, data loss is not an option. <strong>Replication</strong> is the process of synchronizing
                        data across multiple servers to provide redundancy and increase data availability.
                    </Typography>
                </Box>

                {/* 2. THE REPLICA SET ANATOMY */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 6 }}>
                        <Diversity3Icon sx={{ color: BRAND.green, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>The Standard 3-Node Architecture</Typography>
                    </Box>



                    <Grid container spacing={4} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={4}>
                            <NodeCard
                                type="PRIMARY" color={BRAND.green}
                                label="The Single Point of Write"
                                description="Receives all write operations. There is only ever ONE primary at a time to ensure data consistency."
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <NodeCard
                                type="SECONDARY" color={BRAND.cyan}
                                label="Data Redundancy"
                                description="Replicates the Primary's Oplog (Operation Log) to maintain an identical copy of the data."
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <NodeCard
                                type="SECONDARY" color={BRAND.cyan}
                                label="Read Scalability"
                                description="Can be configured to handle read requests, offloading traffic from the Primary node."
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. AUTOMATIC FAILOVER */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <SensorsIcon sx={{ color: BRAND.green, fontSize: 32 }} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Heartbeats & Elections</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            Replica set members send <strong>Heartbeats</strong> (pings) to each other every 2 seconds.
                            If the Primary fails, the Secondaries hold an <strong>Election</strong> to choose a new Primary automatically.
                        </Typography>
                        <Stack spacing={2}>
                            <Paper sx={{ p: 2, bgcolor: "rgba(0,237,100,0.05)", border: `1px solid ${BRAND.border}` }}>
                                <Typography variant="subtitle2" sx={{ color: BRAND.green }}>Self-Healing Cluster</Typography>
                                <Typography variant="caption" sx={{ color: BRAND.muted }}>The application reconnects to the new primary without human intervention.</Typography>
                            </Paper>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>

                    </Grid>
                </Grid>

                {/* 4. READ PREFERENCES */}
                <Box sx={{ mb: 10, p: 4, borderRadius: 6, bgcolor: "rgba(0, 224, 255, 0.03)", border: "1px solid rgba(0, 224, 255, 0.2)" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <SpeedIcon sx={{ color: BRAND.cyan }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Read Preferences</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: BRAND.muted, mb: 4 }}>
                        By default, clients read from the Primary. However, you can tune your <strong>Read Preference</strong> to optimize for global latency or data freshness.
                    </Typography>

                    <Grid container spacing={2}>
                        {[
                            { name: "primary", desc: "Always read from the primary (Strict Consistency)." },
                            { name: "primaryPreferred", desc: "Read from primary if available, otherwise secondary." },
                            { name: "secondary", desc: "Always read from a secondary (Eventual Consistency)." },
                            { name: "nearest", desc: "Read from the node with the lowest network latency." }
                        ].map((pref, i) => (
                            <Grid item xs={12} md={6} key={i}>
                                <Stack direction="row" spacing={2} sx={{ p: 2, bgcolor: BRAND.surface, borderRadius: 2 }}>
                                    <Chip label={pref.name} size="small" sx={{ bgcolor: BRAND.cyan, color: BRAND.bg, fontWeight: 900 }} />
                                    <Typography variant="caption" sx={{ color: BRAND.muted }}>{pref.desc}</Typography>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 5. THE OPLOG PRO-TIP */}
                <Box sx={{ mb: 10 }}>
                    <Paper sx={{ p: 3, bgcolor: "rgba(255,255,255,0.02)", border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                        <Stack direction="row" spacing={2}>
                            <Avatar sx={{ bgcolor: BRAND.green }}><SyncIcon /></Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700 }}>Understanding the Oplog</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, mt: 1 }}>
                                    The <strong>Oplog</strong> (Operation Log) is a special capped collection that keeps a rolling record of all
                                    data modifications. It is the "secret sauce" that allows Secondaries to stay in sync.
                                    If a Secondary falls too far behind the Oplog, it may require a full resync!
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #00684A)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <ShieldIcon style={{ fontSize: 40, color: BRAND.green, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Uptime Guaranteed?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        Mastering Replica Sets is the key to building resilient, production-ready applications.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" sx={{ bgcolor: BRAND.green, color: BRAND.bg, fontWeight: 900 }}>Simulate Failover</Button>
                        <Button variant="outlined" sx={{ color: BRAND.white, borderColor: BRAND.muted }}>Cluster Best Practices</Button>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
